import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLocale } from "@/contexts/LocaleContext";
import { getPageSeo, clampDescription } from "@/lib/page-seo";

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  noIndex?: boolean;
  ogImage?: string;
  ogType?: string;
  locale?: string;
  rawTitle?: boolean;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  canonical,
  noIndex = false,
  ogImage,
  ogType = "website",
  locale: localeProp,
  rawTitle = false,
}) => {
  const location = useLocation();
  const { locale: ctxLocale } = useLocale();
  const locale = localeProp ?? (ctxLocale === "en" ? "en_GB" : "de_DE");
  const brand = "Lerch Suites";
  const cleanTitle = title?.trim();

  /** Redaktionelle Vorgaben (nur DE) haben Vorrang: eindeutige Titles & 120–158 Zeichen. */
  const pageSeo = ctxLocale === "en" ? undefined : getPageSeo(location.pathname);

  const fallbackTitle = (() => {
    if (rawTitle && cleanTitle) return cleanTitle;
    const stripped = cleanTitle
      ?.replace(new RegExp(`\\s*[–|-]\\s*${brand}\\s*$`, "i"), "")
      ?.trim();
    const pageName = stripped && stripped.length > 0 ? stripped : brand;
    return pageName.toLowerCase().includes(brand.toLowerCase())
      ? pageName
      : `${pageName} | ${brand}`;
  })();

  const fullTitle = pageSeo?.title ?? fallbackTitle;
  const metaDescription =
    pageSeo?.description ??
    (description ? clampDescription(description) : undefined);
  const currentUrl = canonical || window.location.origin + location.pathname;
  /** Sprachvarianten laufen über `?lang=en`; „/“ liefert die statischen Tags aus index.html. */
  const emitHreflang = !noIndex && location.pathname !== "/";
  const deUrl = currentUrl.split("?")[0];
  const enUrl = `${deUrl}?lang=en`;

  return (
    <Helmet>
      <html lang={locale.split("_")[0]} />
      <title>{fullTitle}</title>
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {metaDescription && <meta name="description" content={metaDescription} />}
      <link rel="canonical" href={currentUrl} />
      {emitHreflang && <link rel="alternate" hrefLang="de-AT" href={deUrl} />}
      {emitHreflang && <link rel="alternate" hrefLang="de" href={deUrl} />}
      {emitHreflang && <link rel="alternate" hrefLang="en" href={enUrl} />}
      {emitHreflang && <link rel="alternate" hrefLang="x-default" href={deUrl} />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:locale" content={locale} />
      {metaDescription && (
        <meta property="og:description" content={metaDescription} />
      )}
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {metaDescription && (
        <meta name="twitter:description" content={metaDescription} />
      )}
      {ogImage && <meta name="twitter:image" content={ogImage} />}

    </Helmet>
  );
};

export default SEOHead;
